/**
 * Reglas de negocio puras de OT (Orden de Trabajo) — modelo VD §4.12 + §5.5.
 * Funciones puras testeables sin UI ni backend.
 */

import type { So, Tarea, Rd, Po, ContratoMarco, Afe, AfeCosto, VeredictoInt } from "@app-types/entities";
import type { Role } from "@constants/roles";
import { puedeTransicionar, TRANSICIONES_SO } from "@constants/estados";
import { leerLimiteSoPct } from "@features/autoridades/matrizPermisos";

// ============================================================================
// Transiciones VD §5.5
// ============================================================================

export function puedeTransicionarSo(actual: So["estado"], destino: So["estado"]): boolean {
  return puedeTransicionar(actual, destino, TRANSICIONES_SO);
}

/** Devuelve los estados destino válidos desde el estado actual. */
export function transicionesValidasSo(actual: So["estado"]): So["estado"][] {
  return [...(TRANSICIONES_SO[actual] ?? [])];
}

// ============================================================================
// Cálculos básicos
// ============================================================================

/** Calcula monto total de una OT desde sus tareas. */
export function calcularMontoTotal(tareas: Tarea[]): number {
  return tareas.reduce((sum, t) => sum + t.cantidad * t.precioUnitario, 0);
}

/** Calcula porcentaje de avance ponderado por monto. */
export function calcularAvancePonderado(tareas: Tarea[]): number {
  if (tareas.length === 0) return 0;
  const totales = tareas.map((t) => t.cantidad * t.precioUnitario);
  const sumaTotal = totales.reduce((a, b) => a + b, 0);
  if (sumaTotal === 0) return 0;
  const avanceMonetario = tareas.reduce(
    (sum, t, i) => sum + (totales[i] ?? 0) * (t.porcentajeAvance / 100),
    0,
  );
  return Math.round((avanceMonetario / sumaTotal) * 1000) / 10; // 1 decimal
}

/** Plazo total en días. */
export function calcularPlazoSoDias(fechaInicio: string, fechaFin: string): number {
  if (!fechaInicio || !fechaFin) return 0;
  const a = new Date(fechaInicio + "T00:00:00").getTime();
  const b = new Date(fechaFin + "T00:00:00").getTime();
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

// ============================================================================
// Alias compuesto AFE-####/PO-####/SO-## (VD §4.12)
// ============================================================================

/**
 * Construye el alias compuesto que muestra la OT con su trazabilidad completa
 * al AFE y a su origen (PO o Contrato Marco).
 *
 * Formato:
 *   AFE-CTFA-00094 / PO-2026-103 / SO-094-001
 *   AFE-CTFA-00094 / CM-2025-001 / SO-094-002
 *
 * Si la OT es adicional, agrega "(adicional)" al final.
 */
export function aliasCompuestoSo(
  so: Pick<So, "codigo" | "afeId" | "poId" | "contratoMarcoId" | "esAdicional">,
  afe: Pick<Afe, "codigo"> | null | undefined,
  po: Pick<Po, "codigo"> | null | undefined,
  contrato: Pick<ContratoMarco, "codigo"> | null | undefined,
): string {
  const afeStr = afe ? `AFE-${afe.codigo}` : "AFE-?";
  const origenStr = so.poId
    ? po
      ? `PO-${po.codigo}`
      : "PO-?"
    : so.contratoMarcoId
      ? contrato
        ? `CM-${contrato.codigo}`
        : "CM-?"
      : "(sin origen)";
  const adicional = so.esAdicional ? " (adicional)" : "";
  return `${afeStr} / ${origenStr} / ${so.codigo}${adicional}`;
}

// ============================================================================
// Reglas de edición / eliminación
// ============================================================================

/**
 * Datos generales de la OT (alcance, fechas, contratista, AFE, contrato origen)
 * son editables sólo mientras la cotización está en construcción.
 */
export function puedeEditarDatosGeneralesSo(estado: So["estado"]): boolean {
  return estado === "EN_COTIZACION";
}

/**
 * El reporte diario de la OT sólo se habilita una vez la línea base está
 * congelada: estado APROBADA. (Si el PL2 la devuelve a EN_REVISION para
 * reprogramar, deja de estar APROBADA y el reporte diario se suspende hasta
 * la reaprobación.)
 */
export function puedeReportarDiarioSo(estado: So["estado"]): boolean {
  return estado === "APROBADA";
}

/**
 * OT eliminable sólo si está en EN_COTIZACION sin avance reportado.
 * Cualquier estado posterior (EN_REVISION en adelante) implica revisión
 * registrada y debe gestionarse vía CANCELADA.
 */
export function puedeEliminarSo(
  so: Pick<So, "estado">,
  tareas: Tarea[],
  /**
   * Reportes diarios de la OT. Son la verdad del avance — `porcentajeAvance`
   * de la tarea es un derivado que puede quedar en cero aunque existan
   * reportes aprobados.
   *
   * En producción se borraron dos OT CANCELADAS que sí tenían reportes
   * aprobados, y quedaron 19 filas colgando de una OT inexistente: los
   * reportes de dos contratistas y 17 tareas por ~267 millones. La guarda
   * anterior no podía verlo porque solo miraba las tareas.
   */
  _rds: Pick<Rd, "estado">[] = [],
): { puede: boolean; motivo?: string } {
  if (so.estado !== "EN_COTIZACION" && so.estado !== "CANCELADA") {
    return {
      puede: false,
      motivo: `No se puede eliminar una OT en estado ${so.estado}. Cancélala primero.`,
    };
  }
  if (tareas.some((t) => t.porcentajeAvance > 0)) {
    return {
      puede: false,
      motivo: "Esta OT tiene tareas con avance reportado. Cancélala antes de eliminar.",
    };
  }
  // Los reportes diarios NO bloquean: se arrastran. Ver `impactoEliminarSo`,
  // que arma el aviso para que la decisión se tome sabiendo qué se lleva por
  // delante.
  return { puede: true };
}

/**
 * Qué se lleva por delante eliminar una OT.
 *
 * Criterio del responsable del PMIS: no bloquear, AVISAR. El aviso enumera lo
 * asociado, se confirma en el mismo cuadro, y si la respuesta es afirmativa se
 * borra todo junto — reportes, fotos y eventos — para mantener la integridad y
 * sostener la limpieza.
 *
 * Existe porque lo contrario ya ocurrió: se borraron dos OT y quedaron 19
 * filas colgando de un id inexistente —los reportes de dos contratistas y 17
 * tareas por ~267 millones—, invisibles en la interfaz e imposibles de limpiar
 * desde la app.
 *
 * CANCELAR no borra nada: la OT cancelada conserva su historia completa. Este
 * recuento es solo para ELIMINAR.
 */
export function impactoEliminarSo(
  tareas: Pick<Tarea, "id">[],
  rds: Pick<Rd, "estado" | "fotos">[],
  eventos: { id: string }[],
): {
  tareas: number;
  rds: number;
  rdsAprobados: number;
  fotos: number;
  eventos: number;
  /** true si hay algo que arrastrar — decide si el aviso debe enumerarlo. */
  hayAsociados: boolean;
  /** Texto listo para el cuadro de confirmación. */
  resumen: string;
} {
  const rdsAprobados = rds.filter((r) => r.estado === "APROBADO").length;
  const fotos = rds.reduce((s, r) => s + (r.fotos ?? []).length, 0);

  const partes: string[] = [];
  if (rds.length > 0) {
    partes.push(
      `${rds.length} reporte(s) diario(s)` +
        (rdsAprobados > 0 ? ` (${rdsAprobados} APROBADO(s))` : ""),
    );
  }
  if (fotos > 0) partes.push(`${fotos} foto(s)`);
  if (eventos.length > 0) partes.push(`${eventos.length} evento(s)`);
  if (tareas.length > 0) partes.push(`${tareas.length} tarea(s)`);

  const hayAsociados = rds.length > 0 || eventos.length > 0 || tareas.length > 0;
  const resumen = hayAsociados
    ? `Se eliminarán también: ${partes.join(", ")}.` +
      (rdsAprobados > 0
        ? ` Los reportes APROBADOS ya ganaron valor en el AFE — al borrarlos ese avance deja de contar.`
        : "")
    : "Esta OT no tiene información asociada.";

  return { tareas: tareas.length, rds: rds.length, rdsAprobados, fotos, eventos: eventos.length, hayAsociados, resumen };
}

// ============================================================================
// Saldo del origen (PO o Contrato Marco) y validación de monto
// ============================================================================

/**
 * Calcula el saldo disponible de la PO origen para emitir más OTs.
 * Solo aplica a POs con `esContratoPuntual=true` (servicio puntual cerrado).
 *
 *   saldo = PO.monto − Σ(monto de OTs no canceladas asociadas a esa PO)
 *
 * OTs adicionales NO consumen saldo de la PO origen; se aprueban como
 * ampliación independiente (dejan huella en el AFE pero no descuentan PO).
 */
export function saldoPoDisponible(
  po: Pick<Po, "id"> & { montoUsd?: number },
  sos: So[],
  excluirSoId?: string,
): { saldo: number; consumido: number } {
  // Cálculo puro en USD: po.montoUsd − Σ so.montoUsd. Sin conversiones runtime.
  const consumido = sos
    .filter(
      (s) =>
        s.poId === po.id &&
        s.estado !== "CANCELADA" &&
        !s.esAdicional &&
        (excluirSoId === undefined || s.id !== excluirSoId),
    )
    .reduce((acc, s) => acc + (s.montoUsd ?? 0), 0);
  return {
    saldo: Math.max(0, (po.montoUsd ?? 0) - consumido),
    consumido,
  };
}

/**
 * Calcula el **consumo histórico informativo** del Contrato Marco.
 *
 *   consumido = Σ(monto de OTs no canceladas emitidas bajo este contrato)
 *   saldo     = max(0, montoTecho − consumido)   (sólo referencia visual)
 *
 * # Importante — el contrato marco NO es un presupuesto a consumir
 *
 * A diferencia de una PO puntual (que sí tiene un presupuesto cerrado para un
 * servicio específico), el Contrato Marco es un **acuerdo de tarifas** vigente
 * durante un periodo largo. Las OTs que se emiten bajo él cubren trabajos
 * puntuales de muchos AFEs y costes; al momento de pedir una cotización no se
 * conoce el costo todavía y, en general, la suma de OTs sobre la vida del
 * contrato rara vez se acerca al `montoTecho` total.
 *
 * Por eso esta función es **estrictamente informativa**: muestra el consumo
 * histórico y un "saldo" indicativo, pero el sistema NO bloquea la emisión de
 * OTs por superar ese saldo. El control real de coste se hace contra el AFE
 * y la PO origen (cuando aplica), no contra el techo del CM.
 */
export function saldoContratoDisponible(
  contrato: Pick<ContratoMarco, "id" | "montoTecho" | "valorYaConsumido">,
  sos: So[],
  excluirSoId?: string,
): { saldo: number; consumido: number } {
  // Las OTs heredan la moneda del Contrato Marco al crearse (ver SoForm),
  // así que sumar `so.monto` está siempre en la moneda del CM. Este saldo
  // es estrictamente informativo: el CM no bloquea por techo. Incluye el
  // valor ya consumido previo al PMIS (valorYaConsumido) si está cargado.
  const consumido =
    (contrato.valorYaConsumido ?? 0) +
    sos
      .filter(
        (s) =>
          s.contratoMarcoId === contrato.id &&
          s.estado !== "CANCELADA" &&
          !s.esAdicional &&
          (excluirSoId === undefined || s.id !== excluirSoId),
      )
      .reduce((acc, s) => acc + s.monto, 0);
  return {
    saldo: Math.max(0, contrato.montoTecho - consumido),
    consumido,
  };
}

/**
 * Valida el monto de la OT frente al saldo del origen.
 *
 *   - Origen **PO puntual**: bloquea si la OT excede el saldo de la PO.
 *     La PO es un presupuesto cerrado para un servicio específico, así que
 *     superarlo requiere emitir OT adicional o ampliar la PO.
 *
 *   - Origen **Contrato Marco**: **NO bloquea**. El CM es un acuerdo de
 *     tarifas vigente; no es un presupuesto a agotar. El saldo del CM se
 *     mantiene como métrica informativa, pero no impide cotizar la OT.
 *
 *   - Origen **ADICIONAL**: nunca bloquea (no descuenta saldo por diseño).
 */
export function validarMontoSoVsOrigen(
  so: Pick<So, "id" | "montoUsd" | "poId" | "contratoMarcoId" | "esAdicional">,
  pos: { id: string; montoUsd?: number }[],
  contratos: { id: string; montoTecho: number }[],
  sos: So[],
): { valido: boolean; saldoDisponible?: number; mensaje?: string } {
  if (so.esAdicional) return { valido: true };
  if (so.poId) {
    const po = pos.find((p) => p.id === so.poId);
    if (!po) return { valido: true };
    // Comparación en USD (commitment canónico), sin reconvertir.
    const { saldo } = saldoPoDisponible(po, sos, so.id);
    if ((so.montoUsd ?? 0) > saldo + 0.01) {
      return {
        valido: false,
        saldoDisponible: saldo,
        mensaje: `La OT (USD ${(so.montoUsd ?? 0).toFixed(2)}) excede el saldo de la PO origen (USD ${saldo.toFixed(2)}). Reduce el monto o emite la OT como adicional.`,
      };
    }
    return { valido: true, saldoDisponible: saldo };
  }
  if (so.contratoMarcoId) {
    // El Contrato Marco es un acuerdo de tarifas: no bloquea por techo.
    // Devolvemos el saldo informativo (en moneda del CM) para que la UI lo
    // muestre, pero la validación siempre pasa.
    const c = contratos.find((cm) => cm.id === so.contratoMarcoId);
    if (!c) return { valido: true };
    const { saldo } = saldoContratoDisponible(c, sos, so.id);
    return { valido: true, saldoDisponible: saldo };
  }
  return { valido: true };
}

// ============================================================================
// Jerarquía Budget / AFE / Coste — coherencia de la OT
// ============================================================================

/**
 * Devuelve el AfeCosto correspondiente al costeCodigo de la OT dentro del AFE.
 * null si el AFE no contempla ese coste.
 */
export function costoDelAfe(
  afe: Pick<Afe, "costos">,
  costeCodigo: string,
): AfeCosto | null {
  return afe.costos.find((c) => c.costeCodigo === costeCodigo) ?? null;
}

/**
 * Valida que la OT sea coherente con la jerarquía Budget → AFE → Coste:
 *   1. El AFE existe y está en estado operativo (APROBADO/EN_EJECUCION/EXTENDIDO).
 *   2. El AFE pertenece a un Budget activo.
 *   3. El costeCodigo de la OT existe en afe.costos[].
 *   4. Las fechas inicio/fin de la OT caben dentro de la vigencia del AFE.
 *
 * La moneda de la OT puede diferir de la del AFE/coste sin bloquear la
 * creación: el commitment se congela en USD/COP al guardar y los agregados
 * cruzan entidades en USD canónico (ver `congelarMonto` en lib/moneda).
 */
export function validarJerarquiaSo(
  so: Pick<So, "afeId" | "costeCodigo" | "fechaInicio" | "fechaFin">,
  afe: Pick<Afe, "id" | "estado" | "costos" | "fechaInicio" | "fechaFin" | "budgetId"> | null,
  budgetActivo: Pick<{ codigo: string; estado: string }, "codigo" | "estado"> | null,
): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (!afe || afe.id !== so.afeId) {
    errores.push("AFE referenciado no existe en la base de datos.");
    return { valido: false, errores };
  }
  const operativos = ["APROBADO", "EN_EJECUCION", "EXTENDIDO"];
  if (!operativos.includes(afe.estado)) {
    errores.push(
      `El AFE está en estado ${afe.estado}; solo se admiten OTs sobre AFEs APROBADO/EN_EJECUCION/EXTENDIDO.`,
    );
  }
  if (budgetActivo && afe.budgetId !== budgetActivo.codigo && budgetActivo.estado === "ACTIVO") {
    // Aviso suave (no bloqueante): el AFE es de un Budget distinto al activo.
    // No es error porque AFEs pueden cruzar años fiscales.
  }
  const coste = costoDelAfe(afe, so.costeCodigo);
  if (!coste) {
    errores.push(
      `El coste ${so.costeCodigo} no existe en el AFE ${afe.id}. Selecciona un coste válido del AFE.`,
    );
  }
  if (so.fechaInicio && afe.fechaInicio && so.fechaInicio < afe.fechaInicio) {
    errores.push(
      `Fecha inicio OT (${so.fechaInicio}) anterior al inicio del AFE (${afe.fechaInicio}).`,
    );
  }
  if (so.fechaFin && afe.fechaFin && so.fechaFin > afe.fechaFin) {
    errores.push(
      `Fecha fin OT (${so.fechaFin}) posterior al fin del AFE (${afe.fechaFin}). Considera ampliar el AFE.`,
    );
  }
  return { valido: errores.length === 0, errores };
}

/**
 * Valida que el origen (PO o Contrato Marco) sea consistente con la OT en
 * términos de vigencia y estado. La moneda no se valida — los agregados
 * cruzan entidades en USD canónico vía `congelarMonto`.
 */
export function validarOrigenSo(
  so: Pick<So, "fechaInicio" | "fechaFin" | "poId" | "contratoMarcoId" | "esAdicional">,
  po: Pick<Po, "id" | "fechaVigenciaFin" | "esContratoPuntual"> | null | undefined,
  contrato: Pick<ContratoMarco, "id" | "vigenciaFin" | "estado"> | null | undefined,
): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (so.esAdicional) return { valido: true, errores };
  if (so.poId) {
    if (!po) {
      errores.push("PO origen no existe.");
    } else {
      if (!po.esContratoPuntual) {
        errores.push("La PO origen no está marcada como contrato puntual; no admite OTs.");
      }
      if (so.fechaFin && po.fechaVigenciaFin && so.fechaFin > po.fechaVigenciaFin) {
        errores.push(
          `Fecha fin OT (${so.fechaFin}) posterior a vigencia PO (${po.fechaVigenciaFin}).`,
        );
      }
    }
  } else if (so.contratoMarcoId) {
    if (!contrato) {
      errores.push("Contrato Marco origen no existe.");
    } else {
      if (contrato.estado !== "VIGENTE") {
        errores.push(`Contrato Marco no vigente (estado ${contrato.estado}).`);
      }
      if (so.fechaFin && contrato.vigenciaFin && so.fechaFin > contrato.vigenciaFin) {
        errores.push(
          `Fecha fin OT (${so.fechaFin}) posterior a vigencia contrato (${contrato.vigenciaFin}).`,
        );
      }
    }
  }
  return { valido: errores.length === 0, errores };
}

/**
 * Verifica que cada tarea de la OT use una tarifa que existe en el contrato
 * marco origen. Solo aplica si origen=CONTRATO_MARCO. Si origen=PO o ADICIONAL,
 * las tareas pueden tener tarifaId vacío (libre).
 */
export function validarTareasContraContrato(
  tareas: Pick<Tarea, "tarifaId" | "codigo">[],
  contrato: Pick<ContratoMarco, "tarifas"> | null,
): { valido: boolean; errores: string[] } {
  if (!contrato) return { valido: true, errores: [] };
  const errores: string[] = [];
  const tarifaIds = new Set(contrato.tarifas.map((t) => t.id));
  for (const t of tareas) {
    if (!t.tarifaId) {
      errores.push(`Tarea ${t.codigo} sin tarifa del contrato marco.`);
    } else if (!tarifaIds.has(t.tarifaId)) {
      errores.push(`Tarea ${t.codigo} referencia tarifa inexistente en el contrato marco.`);
    }
  }
  return { valido: errores.length === 0, errores };
}

// ============================================================================
// Autoridades VD §3.3 — límites por rol y escalamiento
// ============================================================================
//
// Nota: el escalamiento por monto absoluto a GTE ("FSrM") fue retirado.
// La revisión sigue dos niveles: PL2 (propone) → PL (aprueba). JEFE y GTE
// pueden aprobar siempre como roles superiores, pero no hay un umbral
// automático que escale al GTE.

/** Roles Tecpetrol con autoridad de aprobación de OT/PO (escalan por monto). */
export const ROLES_APROBADOR_SO: Role[] = ["PL2", "PL", "CFE", "JEFE", "GTE"];

/**
 * Límite de aprobación del rol: % de sobre-costo sobre el saldo disponible del
 * coste que el rol puede autorizar. Se lee de la fila "Límite OT / PO" de la
 * matriz de permisos (módulo Autoridades), editable por el ADMIN_PMIS — la
 * matriz es la fuente de verdad de los límites (VD §3.3).
 *
 * Devuelve `null` si el rol NO tiene autoridad de aprobación de OT.
 */
export function limiteAprobacionSoPct(role: Role | null): number | null {
  if (!role || !ROLES_APROBADOR_SO.includes(role)) return null;
  return leerLimiteSoPct(role);
}

/**
 * Sobre-costo de una OT respecto al saldo disponible del coste, en %.
 *   saldoDisponible = valor del coste − Σ(consumido/comprometido por las
 *   demás PR/PO/OT del mismo coste).
 *   - OT cabe en el saldo  → 0
 *   - OT lo excede         → % por encima del saldo
 *   - saldo ≤ 0 con monto  → Infinity (cualquier monto lo excede)
 */
export function sobrecostoPct(
  montoSoUsd: number,
  saldoDisponibleUsd: number,
): number {
  if (montoSoUsd <= saldoDisponibleUsd) return 0;
  if (saldoDisponibleUsd <= 0) return Number.POSITIVE_INFINITY;
  return ((montoSoUsd - saldoDisponibleUsd) / saldoDisponibleUsd) * 100;
}

/**
 * Determina si un rol puede aprobar una OT dado su monto y el saldo disponible
 * del coste. La autoridad se valida contra el límite de la matriz: el rol
 * aprueba si el sobre-costo de la OT no excede su límite.
 *   - PL2 (límite 0%) aprueba sólo OTs que caben en el saldo disponible.
 *   - PL/CFE (≤5%), JEFE (≤10%), GTE (∞) aprueban sobre-costos crecientes.
 *
 * El control de sobre-costo solo aplica si el coste tiene un presupuesto
 * cargado en el AFE (`presupuestoCargado`). Si no hay valor cargado, no se
 * fuerza escalamiento: el PL2 aprueba normalmente (no hay límite contra el
 * cual medir el sobre-costo).
 */
export function puedeAprobarSo(args: {
  role: Role | null;
  montoSoUsd: number;
  saldoDisponibleUsd: number;
  /** ¿El coste tiene un valor cargado en el AFE? Default true (compat). */
  presupuestoCargado?: boolean;
}): { puede: boolean; motivo?: string } {
  const { role, montoSoUsd, saldoDisponibleUsd } = args;
  if (!role) return { puede: false, motivo: "Sin rol asignado." };
  const limitePct = limiteAprobacionSoPct(role);
  if (limitePct === null) {
    return { puede: false, motivo: `El rol ${role} no aprueba OTs.` };
  }
  // Sin presupuesto cargado en el AFE no hay base para medir sobre-costo:
  // el rol aprobador (incluido el PL2) puede aprobar sin escalar.
  if (args.presupuestoCargado === false) return { puede: true };
  const sobre = sobrecostoPct(montoSoUsd, saldoDisponibleUsd);
  if (sobre > limitePct) {
    const limTxt =
      limitePct === Number.POSITIVE_INFINITY ? "ilimitado" : `${limitePct}%`;
    return {
      puede: false,
      motivo: `La OT sobre-pasa el saldo disponible del coste; el rol ${role} sólo autoriza un sobre-costo de ${limTxt}.`,
    };
  }
  return { puede: true };
}

// ============================================================================
// Validación completa de OT
// ============================================================================

/**
 * Validación de la OT al crearla/editar el encabezado.
 *
 * NOTA: el monto NO es obligatorio en esta validación. El monto se construye
 * desde las tareas que cotiza el contratista durante EN_COTIZACION; cuando el
 * contratista envía la cotización a revisión, se exige monto > 0 vía
 * `validarCotizacionSo` (la otra función de este archivo).
 */
export function validarSo(
  s: Pick<
    So,
    | "codigo"
    | "afeId"
    | "costeCodigo"
    | "nombre"
    | "alcance"
    | "ubicacion"
    | "contratistaId"
    | "interventorId"
    | "fechaInicio"
    | "fechaFin"
    | "poId"
    | "contratoMarcoId"
    | "esAdicional"
    | "parentSoId"
  >,
): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (!s.codigo?.trim()) errores.push("Código es obligatorio");
  if (!s.afeId) errores.push("AFE asociado es obligatorio");
  if (!s.costeCodigo) errores.push("Clase de coste es obligatoria");
  if (!s.nombre?.trim()) errores.push("Nombre es obligatorio");
  if (!s.alcance?.trim()) errores.push("Alcance es obligatorio");
  if (!s.ubicacion?.trim()) errores.push("Ubicación es obligatoria");
  if (!s.contratistaId?.trim()) errores.push("Contratista es obligatorio");
  if (!s.interventorId?.trim()) errores.push("Interventor es obligatorio");
  if (!s.fechaInicio) errores.push("Fecha de inicio es obligatoria");
  if (!s.fechaFin) errores.push("Fecha de fin es obligatoria");
  if (s.fechaInicio && s.fechaFin && s.fechaFin < s.fechaInicio) {
    errores.push("Fecha de fin debe ser posterior a la de inicio");
  }
  // Origen: OT normal requiere PO o Contrato Marco; OT adicional requiere parentSoId
  if (s.esAdicional) {
    if (!s.parentSoId) {
      errores.push("Una OT adicional requiere referencia a la OT original (parentSoId).");
    }
  } else {
    if (!s.poId && !s.contratoMarcoId) {
      errores.push("La OT debe tener una PO origen o un Contrato Marco origen.");
    }
    if (s.poId && s.contratoMarcoId) {
      errores.push("La OT no puede tener PO y Contrato Marco simultáneamente; elige uno.");
    }
  }
  return { valido: errores.length === 0, errores };
}

/**
 * Validación de la cotización al enviarla a revisión: el contratista debe
 * haber cargado al menos una tarea y el monto resultante debe ser > 0.
 */
export function validarCotizacionSo(
  s: Pick<So, "monto">,
  tareas: { cantidad: number; precioUnitario: number }[],
): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (tareas.length === 0) {
    errores.push("Debe registrar al menos una tarea cotizada antes de enviar a revisión.");
  }
  if (!s.monto || s.monto <= 0) {
    errores.push("Monto cotizado debe ser mayor a cero.");
  }
  return { valido: errores.length === 0, errores };
}

// ============================================================================
// Programación: Gantt, Curva S y Flujo de caja (helpers puros)
// ============================================================================

/*
 * MOTOR FANTASMA ELIMINADO (2026-08-09).
 *
 * Aquí vivían `distribuirMontoPorDia`, `calcularCurvaS` y `calcularFlujoCaja`,
 * más el helper `diasInclusivos` que solo ellas usaban: una segunda
 * implementación de la curva S —semanal y solo de plan— sin ningún consumidor
 * en producción. Verificado por búsqueda global: únicamente la tocaba su
 * propio test.
 *
 * El peligro no era que corriera, porque no corría. Era que `calcularCurvaS`
 * tenía el NOMBRE EXACTO del motor real, en `features/so/cotizacionCurvaS.ts`.
 * Un import mal autocompletado la habría activado sin error de compilación, y
 * la pantalla habría pintado otra curva: otra fórmula, otro insumo y sin el
 * tope del 100 %.
 *
 * El cálculo de EVM vive en UN solo sitio: cotizacionCurvaS.ts + cotizacionEvm.ts.
 */


/**
 * Validación blanda de predecesoras: detecta tareas que comienzan antes de
 * que su(s) predecesora(s) hayan terminado. No bloquea — devuelve avisos.
 */
export function validarPredecesoras(
  tareas: Pick<Tarea, "id" | "codigo" | "fechaInicio" | "fechaFin" | "predecesoras">[],
): Array<{ codigo: string; mensaje: string }> {
  const byId = new Map(tareas.map((t) => [t.id, t]));
  const avisos: Array<{ codigo: string; mensaje: string }> = [];
  for (const t of tareas) {
    for (const predId of t.predecesoras ?? []) {
      const pred = byId.get(predId);
      if (!pred) continue;
      if (t.fechaInicio < pred.fechaFin) {
        avisos.push({
          codigo: t.codigo,
          mensaje: `inicia ${t.fechaInicio} antes de que ${pred.codigo} termine (${pred.fechaFin}).`,
        });
      }
    }
  }
  return avisos;
}

// ============================================================================
// Detección de problemas en ejecución
// ============================================================================

/** Detecta tareas con avance > 100%. */
export function detectarSobreEjecucion(tareas: Tarea[]): Tarea[] {
  return tareas.filter((t) => t.porcentajeAvance > 100);
}

/** Detecta tareas vencidas no completadas. */
export function detectarTareasVencidas(tareas: Tarea[], hoy = new Date()): Tarea[] {
  const hoyIso = hoy.toISOString().slice(0, 10);
  return tareas.filter((t) => t.fechaFin < hoyIso && t.porcentajeAvance < 100);
}

/** Detecta OTs cuyo plazo se cumplió sin cerrar (alerta operativa). */
export function detectarSosVencidas(sos: So[], hoy = new Date()): So[] {
  const hoyIso = hoy.toISOString().slice(0, 10);
  return sos.filter((s) => s.estado === "APROBADA" && s.fechaFin < hoyIso);
}

// ============================================================================
// KPIs
// ============================================================================

export function kpisSos(
  sos: So[],
  trmPorAfe?: Map<string, number>,
): {
  total: number;
  enCotizacion: number;
  enRevision: number;
  aprobadas: number;
  /** Sub-conjunto de EN_REVISION que son reaprobaciones (reprogramación). */
  enReprogramacion: number;
  cerradas: number;
  canceladas: number;
  adicionales: number;
  vencidas: number;
  /** Monto agregado por moneda solo de OTs vivas. */
  montoPorMoneda: Record<string, number>;
  /** Monto total convertido a USD usando TRM del AFE. */
  montoTotalUsd: number;
} {
  const vivas = sos.filter((s) => s.estado !== "CANCELADA");
  const montoPorMoneda: Record<string, number> = {};
  let montoTotalUsd = 0;
  for (const s of vivas) {
    montoPorMoneda[s.moneda] = (montoPorMoneda[s.moneda] ?? 0) + s.monto;
    if (s.moneda === "USD") {
      montoTotalUsd += s.monto;
    } else {
      const trm = trmPorAfe?.get(s.afeId);
      const trmEff = trm && trm > 0 ? trm : 4200;
      montoTotalUsd += s.monto / trmEff;
    }
  }
  return {
    total: sos.length,
    enCotizacion: sos.filter((s) => s.estado === "EN_COTIZACION").length,
    enRevision: sos.filter((s) => s.estado === "EN_REVISION").length,
    aprobadas: sos.filter((s) => s.estado === "APROBADA").length,
    enReprogramacion: sos.filter(
      (s) => s.estado === "EN_REVISION" && s.enReprogramacion === true,
    ).length,
    cerradas: sos.filter((s) => s.estado === "CERRADA").length,
    canceladas: sos.filter((s) => s.estado === "CANCELADA").length,
    adicionales: sos.filter((s) => s.esAdicional).length,
    vencidas: detectarSosVencidas(sos).length,
    montoPorMoneda,
    montoTotalUsd: Math.round(montoTotalUsd * 100) / 100,
  };
}

// ============================================================================
// Concepto de Interventoría sobre la cotización
// ============================================================================
//
// El Interventor emite un CONCEPTO mientras la OT está EN_REVISION. Aprobar
// deja constancia y no mueve la OT — el PL2 conserva la última palabra.
// Rechazar sí devuelve la cotización al contratista: es el primer filtro.

/** ¿La OT está en un estado donde el Interventor puede conceptuar? */
export function esConceptuablePorInt(estado: So["estado"]): boolean {
  return estado === "EN_REVISION";
}

export type EstadoConceptoInt =
  | "APROBADO"
  | "RECHAZADO"
  | "SOLO_OBSERVACIONES"
  | "SIN_CONCEPTO";

/**
 * Clasifica el concepto de Interventoría de una OT. `SOLO_OBSERVACIONES` cubre
 * el dato heredado: texto sin veredicto explícito.
 */
export function estadoConceptoInt(
  so: Pick<So, "veredictoInt" | "conceptoInt">,
): EstadoConceptoInt {
  if (so.veredictoInt === "APROBADO") return "APROBADO";
  if (so.veredictoInt === "RECHAZADO") return "RECHAZADO";
  return so.conceptoInt && so.conceptoInt.trim() ? "SOLO_OBSERVACIONES" : "SIN_CONCEPTO";
}

/** Etiqueta del semáforo que ve el PL2, con su color institucional. */
export function etiquetaConceptoInt(so: Pick<So, "veredictoInt" | "conceptoInt">): {
  estado: EstadoConceptoInt;
  texto: string;
  color: string;
} {
  const estado = estadoConceptoInt(so);
  switch (estado) {
    case "APROBADO":
      return { estado, texto: "Interventoría aprobó", color: "#2A954F" };
    case "RECHAZADO":
      return { estado, texto: "Interventoría rechazó", color: "#C0392B" };
    case "SOLO_OBSERVACIONES":
      return { estado, texto: "Interventoría dejó observaciones", color: "#093C71" };
    default:
      return {
        estado,
        texto: "Sin concepto de Interventoría — usted puede decidir igual",
        color: "#8A8886",
      };
  }
}

/**
 * Texto que queda en el historial de la OT. Es la constancia permanente: los
 * campos del concepto se limpian en cada ronda, el historial no.
 */
export function notaHistorialConceptoInt(
  veredicto: So["veredictoInt"],
  texto?: string,
): string {
  const base =
    veredicto === "APROBADO"
      ? "Aprobado por Interventoría"
      : veredicto === "RECHAZADO"
        ? "Rechazado por Interventoría"
        : "Concepto de Interventoría sobre la cotización";
  const t = texto?.trim();
  return t ? `${base} — ${t}` : base;
}

/** Campos de la OT que se limpian al ABRIR una ronda nueva de revisión. */
export const CAMPOS_CONCEPTO_INT = [
  "conceptoInt",
  "aprobadoPorInt",
  "veredictoInt",
  "fechaConceptoInt",
  "montoConceptoInt",
] as const;

export interface FlagsConceptoInt {
  conceptoInt?: string;
  veredictoInt?: VeredictoInt;
  aprobadoPorInt?: string;
  fechaConceptoInt?: string;
  montoConceptoInt?: number;
}

/**
 * Calcula los campos a escribir cuando Interventoría emite concepto o veredicto.
 * Única fuente de la regla: los tres adaptadores la comparten para no divergir.
 *
 * Valida que la OT esté EN_REVISION — un concepto sobre una OT ya aprobada,
 * cerrada o cancelada no tiene sentido y la interfaz no es barrera suficiente.
 *
 * Nota: la validación de IDENTIDAD (que quien firma sea el interventor de la OT)
 * requiere la lista de usuarios para resolver al back, que el repositorio no
 * tiene. Se resuelve en el bloque E2, del lado del servidor, que es donde puede
 * garantizarse.
 */
export function cambiosConceptoInt(
  so: Pick<So, "estado" | "monto" | "conceptoInt">,
  flags: FlagsConceptoInt,
  userEmail: string,
  ahora: string = new Date().toISOString(),
): { cambios: Partial<So>; nota: string } {
  if (!esConceptuablePorInt(so.estado)) {
    throw new Error(
      `El concepto de Interventoría solo aplica a OT en revisión (esta está ${so.estado}).`,
    );
  }
  const cambios: Partial<So> = {};
  // Asignación CONDICIONAL: aprobar sin escribir texto no debe borrar las
  // observaciones que el Interventor ya había dejado. El diálogo entrega cadena
  // VACÍA cuando el campo se deja en blanco (no `undefined`), así que hay que
  // descartarla explícitamente: sin esto, "Aprobar" sin comentario borraba el
  // texto de la ronda en curso y el heredado de las OT que ya están en producción.
  if (flags.conceptoInt !== undefined && flags.conceptoInt.trim() !== "") {
    cambios.conceptoInt = flags.conceptoInt;
  }
  if (flags.veredictoInt !== undefined) {
    cambios.veredictoInt = flags.veredictoInt;
    cambios.fechaConceptoInt = flags.fechaConceptoInt ?? ahora;
    cambios.montoConceptoInt = flags.montoConceptoInt ?? so.monto;
  }
  if (flags.conceptoInt !== undefined || flags.veredictoInt !== undefined) {
    cambios.aprobadoPorInt = flags.aprobadoPorInt ?? userEmail;
  }
  const texto = flags.conceptoInt ?? so.conceptoInt;
  return { cambios, nota: notaHistorialConceptoInt(flags.veredictoInt, texto) };
}

// ============================================================================
// Reprogramación de una OT con avance ejecutado (reglas R1–R5)
// ============================================================================

/**
 * Cantidad ya ejecutada por tarea, según los reportes diarios APROBADOS.
 *
 * Es la única fuente válida de "lo ejecutado" — la misma que alimenta el valor
 * ganado. `Tarea.cantidadEjecutada` y `Tarea.porcentajeAvance` NO sirven:
 * ningún flujo los actualiza desde los reportes y sus únicos valores distintos
 * de cero vienen del sembrado.
 */
export function ejecutadoPorTarea(
  rds: { estado: string; avancesTareas?: { tareaId: string; cantidadDia?: number }[] }[],
): Map<string, number> {
  const acc = new Map<string, number>();
  for (const rd of rds) {
    if (rd.estado !== "APROBADO") continue;
    for (const a of rd.avancesTareas ?? []) {
      const c = Number(a.cantidadDia);
      if (!Number.isFinite(c) || c === 0) continue;
      acc.set(a.tareaId, (acc.get(a.tareaId) ?? 0) + c);
    }
  }
  return acc;
}

export interface ReprogramacionInvalida {
  tareaId: string;
  codigo: string;
  regla: "R1" | "R3";
  mensaje: string;
}

/**
 * Valida una reprogramación contra las reglas R1–R5.
 *
 *   R1  Prohibido ELIMINAR una tarea que ya tiene avance reportado.
 *   R2  Permitido eliminar las que no tienen avance.
 *   R3  Permitido AJUSTAR la cantidad planeada, pero NUNCA por debajo de lo
 *       ya ejecutado. Puede quedar igual, mayor, o con otro plazo.
 *   R4  Permitido ADICIONAR tareas y grupos.
 *   R5  Permitido MODIFICAR el plazo de todas las tareas.
 *
 * Solo R1 y R3 son restricciones; R2, R4 y R5 son permisos y no requieren
 * validación. Reducir una cantidad por debajo de lo ejecutado, o borrar una
 * tarea con avance, deja los reportes apuntando a algo que ya no existe o que
 * contradice el hecho cumplido: el valor ganado se degrada en silencio porque
 * se cruza `avancesTareas.tareaId` contra la lista de tareas.
 *
 * NO bloquea al contratista: el criterio del responsable es que pueda guardar
 * parciales con la inconsistencia visible. Lo que se bloquea es que el PL2
 * REAPRUEBE la línea base mientras exista una contradicción — una línea base
 * aprobada nunca puede contradecir el trabajo ya ejecutado.
 */
export function validarReprogramacion(
  tareasNuevas: Pick<Tarea, "id" | "codigo" | "cantidad">[],
  tareasExistentes: Pick<Tarea, "id" | "codigo">[],
  rds: { estado: string; avancesTareas?: { tareaId: string; cantidadDia?: number }[] }[],
): { valido: boolean; problemas: ReprogramacionInvalida[] } {
  const ejecutado = ejecutadoPorTarea(rds);
  const idsNuevos = new Set(tareasNuevas.map((t) => t.id).filter(Boolean));
  const problemas: ReprogramacionInvalida[] = [];

  // R1 — eliminadas con avance.
  for (const t of tareasExistentes) {
    if (idsNuevos.has(t.id)) continue;
    const ej = ejecutado.get(t.id) ?? 0;
    if (ej > 0) {
      problemas.push({
        tareaId: t.id,
        codigo: t.codigo,
        regla: "R1",
        mensaje: `La tarea ${t.codigo} tiene ${ej} ejecutado en reportes aprobados: no se puede eliminar.`,
      });
    }
  }

  // R3 — cantidad por debajo de lo ejecutado.
  for (const t of tareasNuevas) {
    const ej = ejecutado.get(t.id) ?? 0;
    if (ej > 0 && t.cantidad < ej - 0.001) {
      problemas.push({
        tareaId: t.id,
        codigo: t.codigo,
        regla: "R3",
        mensaje: `La tarea ${t.codigo} ya tiene ${ej} ejecutado: la cantidad planeada no puede quedar en ${t.cantidad}.`,
      });
    }
  }

  return { valido: problemas.length === 0, problemas };
}

/**
 * Tareas cuya cantidad planeada quedó POR DEBAJO de lo ya ejecutado (R3).
 *
 * Se separa de `validarReprogramacion` a propósito, porque las dos reglas se
 * aplican en momentos distintos:
 *
 *   R1 (eliminar una tarea con avance) es DESTRUCTIVO e irreversible: se
 *      impide al guardar.
 *   R3 (cantidad por debajo de lo ejecutado) es una INCONSISTENCIA: el
 *      contratista puede guardar el parcial y verla señalada mientras trabaja
 *      —no se le traba la reprogramación a medio hacer—, pero el PL2 no puede
 *      REAPROBAR la línea base mientras exista. Una línea base aprobada no
 *      puede contradecir el trabajo ya ejecutado.
 */
export function inconsistenciasLineaBase(
  tareas: Pick<Tarea, "id" | "codigo" | "cantidad">[],
  rds: { estado: string; avancesTareas?: { tareaId: string; cantidadDia?: number }[] }[],
): ReprogramacionInvalida[] {
  return validarReprogramacion(tareas, [], rds).problemas.filter((p) => p.regla === "R3");
}
